import { useContext, useEffect, useRef } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { UserContext } from "../../Context/Context";
import { attachEcho } from "../../../api/axios";

window.Pusher = Pusher;
Pusher.logToConsole = process.env.NODE_ENV === "development";

const asBool = (value, fallback = true) => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value).toLowerCase() === "true";
};

const resolveEchoConfig = () => {
  const wsEnabled = asBool(process.env.REACT_APP_ENABLE_ECHO, true);
  const wsHost =
    process.env.REACT_APP_HOST_NAME ||
    process.env.REACT_APP_PUSHER_HOST ||
    "localhost";

  const isLocalSocketHost = ["localhost", "127.0.0.1", "::1"].includes(
    String(wsHost).toLowerCase(),
  );

  const parsedPort = Number(process.env.REACT_APP_PUSHER_PORT);
  const wsPort = Number.isFinite(parsedPort)
    ? parsedPort
    : isLocalSocketHost
      ? 6001
      : 443;

  const forceTLS = !isLocalSocketHost;

  return {
    wsEnabled,
    wsHost,
    wsPort,
    forceTLS,
    transports: forceTLS ? ["wss"] : ["ws", "wss"],
    authEndpoint: `${process.env.REACT_APP_BACKEND_HOST}/broadcasting/auth`,
  };
};

export function useEcho() {
  const { userData } = useContext(UserContext);
  const { user, token } = userData;
  const echoRef = useRef(null);
  const channelsRef = useRef([]);

  useEffect(() => {
    if (!user) return;

    const { wsEnabled, wsHost, wsPort, forceTLS, transports, authEndpoint } =
      resolveEchoConfig();

    if (!wsEnabled) {
      attachEcho(null);
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.info("[Echo] Config", {
        key: process.env.REACT_APP_PUSHER_APP_KEY,
        wsHost,
        wsPort,
        forceTLS,
      });
    }

    // const echo = new Echo({
    //   broadcaster: "pusher",
    //   key: process.env.REACT_APP_PUSHER_APP_KEY,
    //   cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
    //   wsHost: process.env.REACT_APP_HOST_NAME,
    //   wsPort: 6001,
    //   forceTLS: false,
    //   authEndpoint: `${process.env.REACT_APP_BACKEND_HOST}/broadcasting/auth`,
    //   auth: {
    //     headers: {
    //       Authorization: `Bearer ${token}`, // token que guardas en React
    //     },
    //   },
    // });

    const echo = new Echo({
      broadcaster: "pusher",
      key: process.env.REACT_APP_PUSHER_APP_KEY,
      cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
      wsHost,
      wsPort,
      wssPort: wsPort,
      forceTLS,
      encrypted: forceTLS,
      enabledTransports: transports,
      disableStats: true,
      authEndpoint,
      auth: {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    echoRef.current = echo;
    attachEcho(echo);

    const pusher = echo.connector.pusher;

    // Logs de conexión
    pusher.connection.bind("connected", () =>
      console.log(
        "%cWebSocket conectado",
        "color: #00c853; font-weight: bold;",
      ),
    );
    pusher.connection.bind("disconnected", () =>
      console.warn(
        "%c WebSocket desconectado",
        "color: #ff9100; font-weight: bold;",
      ),
    );
    pusher.connection.bind("error", (err) =>
      console.error(
        "%c Error WebSocket:",
        "color: #d50000; font-weight: bold;",
        err,
      ),
    );
    pusher.connection.bind("subscription_error", (err) =>
      console.error(
        "%c Error de suscripción (Auth falló):",
        "color: #ff1744; font-weight: bold;",
        err,
      ),
    );

    // Suscribirse al canal del usuario
    const userChannel = echo.private(`user.${user.id}`);

    const handleIncomingNotification = (e) => {
      console.log("Nueva notificación:", e);
      window.dispatchEvent(
        new CustomEvent("messages:new-notification", { detail: e }),
      );
    };

    // Compatibilidad: algunos entornos requieren prefijo "." con broadcastAs.
    userChannel.listen(".NewNotificationEvent", handleIncomingNotification);
    userChannel.listen("NewNotificationEvent", handleIncomingNotification);
    channelsRef.current.push(userChannel);

    return () => {
      channelsRef.current.forEach((ch) => ch.unsubscribe());
      channelsRef.current = [];
      echoRef.current.disconnect();
      echoRef.current = null;
      attachEcho(null);
    };
  }, [token, user?.id]);

  const subscribePrivateChannel = (channelName, eventName, callback) => {
    if (!echoRef.current) return null;
    const channel = echoRef.current
      .private(channelName)
      .listen(eventName, callback);
    channelsRef.current.push(channel);
    return channel;
  };

  return { subscribePrivateChannel };
}
